import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaGraduationCap, FaBuilding, FaArrowRight, FaQuoteLeft, FaUserTie, FaGlobe, FaShieldAlt, FaCertificate, FaHome, FaLanguage } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const events = [
  {
    image: "https://images.unsplash.com/photo-1594750852517-f37738fa2384?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwZ3JhZHVhdGlvbiUyMGNlcmVtb255JTIwc3R1ZGVudHMlMjBoYXBweXxlbnwwfHx8fDE3Njg2NzcyMTd8MA&ixlib=rb-4.1.0&q=85",
    title: "Cérémonie de Graduation 2024",
    description: "Félicitations à nos diplômés!"
  },
  {
    image: "https://images.unsplash.com/photo-1648301033733-44554c74ec50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxhZnJpY2FuJTIwdW5pdmVyc2l0eSUyMHN0dWRlbnRzJTIwc3R1ZHlpbmclMjBjYW1wdXN8ZW58MHx8fHwxNzY4Njc3MjEwfDA&ixlib=rb-4.1.0&q=85",
    title: "Vie de Campus",
    description: "Un environnement d'apprentissage moderne et convivial"
  },
  {
    image: "https://images.unsplash.com/photo-1692883702706-a50fc21da44f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIwZ3JhZHVhdGlvbiUyMGNlcmVtb255JTIwc3R1ZGVudHMlMjBoYXBweXxlbnwwfHx8fDE3Njg2NzcyMTd8MA&ixlib=rb-4.1.0&q=85",
    title: "Excellence Académique",
    description: "Former les leaders de demain"
  }
];

const filieres = [
  { name: "Droit", icon: "⚖️", path: "/filieres/droit" },
  { name: "Sciences Économiques", icon: "📊", path: "/filieres/economie" },
  { name: "Marketing et Management", icon: "💼", path: "/filieres/marketing-management" },
  { name: "Sciences Informatiques", icon: "💻", path: "/filieres/informatique" },
  { name: "Communication", icon: "📢", path: "/filieres/communication" },
  { name: "GRH", icon: "👥", path: "/filieres/grh" },
  { name: "Logistique", icon: "🚛", path: "/filieres/logistique" },
  { name: "Finance", icon: "💰", path: "/filieres/finance" },
  { name: "Sciences Comptables", icon: "📒", path: "/filieres/comptabilite" },
  { name: "Science Politique", icon: "🏛️", path: "/filieres/science-politique" },
  { name: "Lettres Modernes", icon: "📚", path: "/filieres/lettres-modernes" },
  { name: "Histoire", icon: "📜", path: "/filieres/histoire" }
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % events.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + events.length) % events.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Event Slider */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={events[currentSlide].image} 
            alt={events[currentSlide].title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="text-white max-w-2xl">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight"
            >
              Bienvenue à l'Université<br />CEFAT International
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl sm:text-2xl font-semibold mb-2 text-brand-accent"
            >
              « L'excellence à proximité »
            </motion.p>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-gray-100"
            >
              {events[currentSlide].description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <a href="https://scolarite-cefat.net/preinscription">
                <Button 
                  data-testid="hero-cta-preinscription"
                  className="bg-brand-primary hover:bg-brand-primary-dark text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-md shadow-xl transition-all hover:shadow-2xl hover:-translate-y-1"
                >
                  Préinscrivez-vous maintenant
                  <FaArrowRight className="ml-2 inline" />
                </Button>
              </a>
            </motion.div>
          </div>
        </div>

        {/* Slider Controls */}
        <button 
          onClick={prevSlide}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all"
        >
          <FaChevronLeft className="text-sm sm:text-base" />
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all"
        >
          <FaChevronRight className="text-sm sm:text-base" />
        </button>
      </section>

      {/* Quick Stats */}
      <section className="py-8 sm:py-12 bg-gradient-to-r from-brand-primary to-brand-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 text-center text-white">
            <div>
              <FaBuilding className="text-4xl sm:text-5xl mx-auto mb-3 sm:mb-4" />
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">5 Campus</h3>
              <p className="text-sm sm:text-base text-gray-100">Présence territoriale forte en Afrique de l'Ouest</p>
            </div>
            <div>
              <FaGraduationCap className="text-4xl sm:text-5xl mx-auto mb-3 sm:mb-4" />
              <h3 className="text-2xl sm:text-3xl font-bold mb-2">+15 Filières</h3>
              <p className="text-sm sm:text-base text-gray-100">Programmes d'excellence et d'employabilité</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mot du Président */}
      <section id="president" className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-3 mb-6">
                <FaQuoteLeft className="text-3xl text-brand-primary/30" />
                <h2 className="text-3xl sm:text-4xl font-bold text-brand-secondary">Mot du Président</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Bienvenue à l'<strong>UNIVERSITÉ CEFAT INTERNATIONAL</strong>, filiale de CEFAT INTERNATIONAL HOLDING.
                </p>
                <p>
                  Dans un environnement mondial en pleine mutation, notre université s'est donné pour mission de former des femmes et des hommes compétents, responsables et ouverts sur le monde, capables de relever les défis du développement de la Côte d'Ivoire et de l'Afrique.
                </p>
                <p>
                  Fidèles à notre slogan <strong>« L'excellence à proximité »</strong>, nous offrons un enseignement supérieur de qualité, accessible et conforme aux standards internationaux, à travers nos cinq campus opérationnels : <strong>Abidjan Cocody – Angré, Abengourou, Bondoukou, Bonoua</strong>, ainsi que notre <strong>Centre d'Immersion Linguistique d'Accra</strong>, au Ghana.
                </p>
                <p>
                  Grâce à nos partenariats internationaux en France, en Grande-Bretagne, en Inde et dans d'autres pays, nos étudiants bénéficient d'une formation ouverte sur l'international, orientée vers l'employabilité, l'innovation et l'excellence.
                </p>
                <p>
                  À l'UNIVERSITÉ CEFAT INTERNATIONAL, nous croyons en la <strong>discipline, l'éthique, le mérite et l'excellence</strong> comme fondements de la réussite.
                </p>
                <p className="text-brand-primary font-semibold">
                  Je vous souhaite la bienvenue et plein succès au sein de notre université.
                </p>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="font-bold text-xl text-brand-secondary">Honoré AMAN</p>
                <p className="text-brand-primary font-medium">Président de l'UNIVERSITÉ CEFAT INTERNATIONAL</p>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative">
                <div className="w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 mx-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_cefat-university/artifacts/c2r5jgob_dg%20cefat.png"
                    alt="Président Honoré AMAN"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-brand-primary text-white px-6 py-2 rounded-full shadow-lg">
                  <p className="text-sm font-semibold whitespace-nowrap">Président Fondateur</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Formations */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-brand-secondary">Nos Formations</h2>
            <p className="text-xl text-brand-primary font-semibold">Licence et Master</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Formation Initiale */}
            <Card className="p-8 bg-white shadow-lg border-t-4 border-brand-primary">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-brand-primary/10 rounded-full flex items-center justify-center">
                  <FaGraduationCap className="text-2xl text-brand-primary" />
                </div>
                <h3 className="text-2xl font-bold text-brand-secondary">Formation Initiale</h3>
              </div>
              <p className="text-gray-600 mb-4">
                <strong>Pour les nouveaux bacheliers</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary mt-1">✓</span>
                  <span>Cours en <strong>présentiel</strong> sur nos campus</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary mt-1">✓</span>
                  <span>Encadrement personnalisé</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary mt-1">✓</span>
                  <span>Diplômes Licence (L1, L2, L3) et Master (M1, M2)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-primary mt-1">✓</span>
                  <span>Enseignants certifiés CAMES</span>
                </li>
              </ul>
            </Card>

            {/* Formation Continue */}
            <Card className="p-8 bg-white shadow-lg border-t-4 border-brand-secondary">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-brand-secondary/10 rounded-full flex items-center justify-center">
                  <FaUserTie className="text-2xl text-brand-secondary" />
                </div>
                <h3 className="text-2xl font-bold text-brand-secondary">Formation Continue</h3>
              </div>
              <p className="text-gray-600 mb-4">
                <strong>Pour les travailleurs et auditeurs</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-brand-secondary mt-1">✓</span>
                  <span>Cours <strong>en ligne</strong> et par <strong>alternance</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-secondary mt-1">✓</span>
                  <span>Travaux Dirigés (TD) les <strong>samedis</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-secondary mt-1">✓</span>
                  <span>Évaluations pendant les <strong>congés</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-secondary mt-1">✓</span>
                  <span>Flexibilité adaptée aux professionnels</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Nos Filières */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-brand-secondary">Nos Filières</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Découvrez nos programmes d'études en Licence et Master
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filieres.map((filiere, index) => (
              <Link to={filiere.path} key={index}>
                <Card 
                  className="p-4 sm:p-6 card-hover text-center bg-white border-gray-200 cursor-pointer h-full"
                >
                  <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">{filiere.icon}</div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-800">{filiere.name}</h3>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8 sm:mt-12">
            <Link to="/filieres">
              <Button 
                className="bg-brand-secondary hover:bg-brand-secondary/90 text-white px-6 sm:px-8 py-3 rounded-md transition-all hover:-translate-y-1"
              >
                Voir toutes les filières
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Nos Campus */}
      <section className="py-16 sm:py-20 md:py-24 bg-brand-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Nos 5 Campus</h2>
            <p className="text-lg text-gray-300">Une présence territoriale forte pour être proche de vous</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { name: "Abidjan Cocody-Angré", country: "Côte d'Ivoire", main: true },
              { name: "Abengourou", country: "Côte d'Ivoire" },
              { name: "Bondoukou", country: "Côte d'Ivoire" },
              { name: "Bonoua", country: "Côte d'Ivoire" },
              { name: "Accra", country: "Ghana", subtitle: "Centre d'Immersion Linguistique" }
            ].map((campus, index) => (
              <Card key={index} className={`p-6 text-center ${campus.main ? 'bg-brand-primary border-brand-accent border-2' : 'bg-white/10 border-white/20'}`}>
                <FaBuilding className="text-3xl mx-auto mb-3 text-brand-accent" />
                <h3 className="font-bold text-lg">{campus.name}</h3>
                <p className="text-sm text-gray-300">{campus.country}</p>
                {campus.subtitle && <p className="text-xs text-brand-accent mt-1">{campus.subtitle}</p>}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-brand-secondary">Pourquoi Choisir CEFAT International ?</h2>
            <p className="text-lg text-gray-600">Les atouts qui font notre différence</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Qualité Enseignante */}
            <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
              <FaUserTie className="text-4xl text-brand-primary mb-4" />
              <h3 className="text-xl font-bold mb-3 text-brand-secondary">Qualité Enseignante</h3>
              <p className="text-gray-700">
                Vous apprenez avec les meilleurs : nos enseignants sont <strong>qualifiés et certifiés CAMES</strong>, garantissant un encadrement de niveau académique supérieur.
              </p>
            </Card>

            {/* Infrastructures */}
            <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
              <FaBuilding className="text-4xl text-brand-primary mb-4" />
              <h3 className="text-xl font-bold mb-3 text-brand-secondary">Infrastructures Modernes</h3>
              <p className="text-gray-700">
                Nous offrons un cadre d'étude moderne avec des <strong>équipements de pointe</strong>, respectant les normes internationales pour une performance optimale.
              </p>
            </Card>

            {/* Mobilité */}
            <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
              <FaGlobe className="text-4xl text-brand-primary mb-4" />
              <h3 className="text-xl font-bold mb-3 text-brand-secondary">Mobilité Internationale</h3>
              <p className="text-gray-700">
                Possibilité de mobilité internationale avec des <strong>universités d'excellence</strong> de Grande-Bretagne, France et Inde. Un atout majeur sur votre CV.
              </p>
            </Card>

            {/* Diplômes */}
            <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
              <FaCertificate className="text-4xl text-brand-primary mb-4" />
              <h3 className="text-xl font-bold mb-3 text-brand-secondary">Diplômes Reconnus</h3>
              <p className="text-gray-700">
                Tous nos diplômes sont <strong>homologués, reconnus par l'État</strong>, et Certifiés CAMES. Choisir CEFAT, c'est choisir la tranquillité d'esprit.
              </p>
            </Card>

            {/* Hébergement */}
            <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
              <FaHome className="text-4xl text-brand-primary mb-4" />
              <h3 className="text-xl font-bold mb-3 text-brand-secondary">Résidences Universitaires</h3>
              <p className="text-gray-700">
                Disponibilité de <strong>résidences universitaires</strong> sur nos campus pour un cadre de vie sécurisé et propice aux études.
              </p>
            </Card>

            {/* Formation Bilingue */}
            <Card className="p-8 bg-white shadow-lg card-hover border-t-4 border-brand-primary">
              <FaLanguage className="text-4xl text-brand-primary mb-4" />
              <h3 className="text-xl font-bold mb-3 text-brand-secondary">Formation Bilingue</h3>
              <p className="text-gray-700">
                Formation bilingue pour tous dans notre <strong>centre de perfectionnement linguistique à Accra</strong> au Ghana, valable pour tous les inscrits.
              </p>
            </Card>
          </div>

          <div className="text-center mt-12">
            <a href="https://scolarite-cefat.net/preinscription">
              <Button 
                className="bg-brand-primary hover:bg-brand-primary-dark text-white px-8 py-6 text-lg font-semibold rounded-md shadow-xl transition-all hover:shadow-2xl hover:-translate-y-1"
              >
                Se préinscrire maintenant
                <FaArrowRight className="ml-2 inline" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-brand-primary to-brand-primary-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Prêt à rejoindre l'excellence ?</h2>
          <p className="text-lg mb-8 text-gray-100">
            Que vous soyez nouveau bachelier ou professionnel en quête de formation continue, CEFAT International vous accompagne vers la réussite.
          </p>
          <a href="https://scolarite-cefat.net/preinscription">
            <Button 
              className="bg-white text-brand-primary hover:bg-brand-accent hover:text-brand-secondary px-8 py-6 text-lg font-semibold rounded-md shadow-xl transition-all hover:shadow-2xl hover:-translate-y-1"
            >
              Commencer ma préinscription
              <FaArrowRight className="ml-2 inline" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
